import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import type { ResponsiveColorsType } from '@/components/Box';
import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import type { StickyLayoutProps } from '@/components/StickyLayout';
import { StickyLayout } from '@/components/StickyLayout';
import type { ContentfulFooterFragment } from '@/graphql/contentful/api';
import { DefaultLayoutDocument } from '@/graphql/contentful/api';
import { theme } from '@/theme/getTheme';

import type { CMSLayoutDataProps } from '../Default';
import type { BreadcrumbItems } from './AccountHeader';
import { AccountHeader } from './AccountHeader';

export interface AccountLayoutProps extends Pick<
  StickyLayoutProps,
  'backgroundColor'
> {
  breadcrumbItems?: BreadcrumbItems[];
  children: React.ReactNode;
  cmsData?: CMSLayoutDataProps;
  hasBreadcrumbs?: boolean;
  logoutLink?: string;
  mainBackgroundColor?: ResponsiveColorsType;
  showClubMessage?: boolean;
}

export const AccountLayout = ({
  backgroundColor,
  breadcrumbItems,
  children,
  cmsData,
  hasBreadcrumbs = false,
  logoutLink,
  mainBackgroundColor = 'white',
  showClubMessage = false,
}: AccountLayoutProps) => {
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: DefaultLayoutDocument,
  });

  return (
    <StickyLayout
      backgroundColor={backgroundColor}
      data-test-id='AccountLayoutStickyLayout'
    >
      {liveCmsData?.header ? (
        <Header
          draftMode={false}
          {...liveCmsData.header}
          cmsSearchOverlayData={liveCmsData.search}
          data-test-id='AccountLayoutHeader'
          isStick={false}
        />
      ) : (
        <Header
          cmsSearchOverlayData={liveCmsData?.search}
          data-test-id='AccountLayoutHeader'
          draftMode={false}
          isStick={false}
        />
      )}
      <StickyLayout.Main
        backgroundColor={mainBackgroundColor}
        data-test-id='AccountLayoutStickyLayoutMain'
        headerBackground={
          liveCmsData?.header?.background ??
          theme.components.header.defaultBackground
        }
      >
        <AccountHeader
          breadcrumbItems={breadcrumbItems}
          data-test-id='AccountLayoutAccountHeader'
          hasBreadcrumbs={hasBreadcrumbs}
          logoutLink={logoutLink}
          showClubMessage={showClubMessage}
        />
        {children}
      </StickyLayout.Main>

      <Footer
        data-test-id='AccountLayoutFooter'
        footerData={(liveCmsData?.footer ?? {}) as ContentfulFooterFragment}
      />
    </StickyLayout>
  );
};

AccountLayout.displayName = 'AccountLayout';
